import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../context/LanguageContext';
import { 
  Shield, AlertTriangle, Eye, Lock, UserCheck, Phone,
  CreditCard, FileText, MessageSquare, Heart, Flame
} from 'lucide-react';

const SafetyPage = () => {
  const { language } = useLanguage();

  const tips = [
    {
      icon: Eye,
      title: language === 'nl' ? 'Bezichtig altijd de woning' : 'Always view the property',
      description: language === 'nl'
        ? 'Huur nooit een woning zonder deze eerst in het echt te hebben gezien. Wees alert op verhuurders die dit weigeren.'
        : 'Never rent a property without seeing it in person first. Be alert to landlords who refuse this.'
    },
    {
      icon: CreditCard,
      title: language === 'nl' ? 'Betaal nooit vooraf' : 'Never pay in advance',
      description: language === 'nl'
        ? 'Betaal geen borg of huur voordat je het contract hebt getekend en de sleutels hebt ontvangen.'
        : 'Don\'t pay deposit or rent before signing the contract and receiving the keys.'
    },
    {
      icon: FileText,
      title: language === 'nl' ? 'Controleer het contract' : 'Check the contract',
      description: language === 'nl'
        ? 'Lees het huurcontract zorgvuldig door. Let op de opzegtermijn, borg en eventuele bijkomende kosten.'
        : 'Read the rental contract carefully. Note the notice period, deposit and any additional costs.'
    },
    {
      icon: UserCheck,
      title: language === 'nl' ? 'Verifieer de verhuurder' : 'Verify the landlord',
      description: language === 'nl'
        ? 'Vraag om identificatie van de verhuurder. Controleer of deze daadwerkelijk eigenaar is of bevoegd is om te verhuren.'
        : 'Ask for landlord identification. Check if they are actually the owner or authorized to rent.'
    },
    {
      icon: MessageSquare,
      title: language === 'nl' ? 'Communiceer via het platform' : 'Communicate via the platform',
      description: language === 'nl'
        ? 'Houd contact via het platform. Zo hebben wij een overzicht bij eventuele geschillen.'
        : 'Keep contact via the platform. This way we have an overview in case of disputes.'
    },
    {
      icon: Phone,
      title: language === 'nl' ? 'Deel geen persoonlijke gegevens' : 'Don\'t share personal data',
      description: language === 'nl'
        ? 'Deel nooit je BSN, bankgegevens of wachtwoorden met verhuurders. Legitieme verhuurders vragen hier niet om.'
        : 'Never share your social security number, bank details or passwords. Legitimate landlords don\'t ask for this.'
    }
  ];

  const warnings = [
    language === 'nl' ? 'De prijs is te mooi om waar te zijn' : 'The price is too good to be true',
    language === 'nl' ? 'De verhuurder is in het buitenland en kan niet bezichtigen' : 'The landlord is abroad and cannot show the property',
    language === 'nl' ? 'Er wordt druk gezet om snel te beslissen' : 'You\'re pressured to decide quickly',
    language === 'nl' ? 'Betaling wordt gevraagd via ongebruikelijke methoden' : 'Payment is requested via unusual methods',
    language === 'nl' ? 'De foto\'s lijken van internet geplukt' : 'The photos seem to be taken from the internet',
    language === 'nl' ? 'Het contract bevat vreemde clausules' : 'The contract contains strange clauses'
  ];

  return (
    <div className="min-h-screen flex flex-col bg-black" data-testid="safety-page">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative py-24 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-rose-600 via-pink-600 to-red-700"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-orange-500/30 via-transparent to-transparent"></div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-6">
              {language === 'nl' ? 'Veilig huren' : 'Safe renting'}
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              {language === 'nl' 
                ? 'Jouw veiligheid is onze prioriteit. Volg deze tips om fraude te voorkomen.'
                : 'Your safety is our priority. Follow these tips to prevent fraud.'}
            </p>
          </div>
        </section>

        {/* Tips */}
        <section className="py-20 px-4 bg-black">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="bg-rose-500/20 text-rose-400 border-0 mb-4">VEILIGHEIDSTIPS</Badge>
              <h2 className="text-3xl font-black text-white">
                {language === 'nl' ? '6 gouden regels voor veilig huren' : '6 golden rules for safe renting'}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tips.map((tip, i) => (
                <Card key={i} className="border border-gray-800 bg-gray-900 hover:border-rose-500/50 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-rose-500/20 rounded-xl flex items-center justify-center mb-4">
                      <tip.icon className="w-7 h-7 text-rose-500" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{tip.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{tip.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Warning Signs */}
        <section className="py-20 bg-red-950/30 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-3xl font-black text-white mb-4">
                {language === 'nl' ? 'Herken de waarschuwingssignalen' : 'Recognize warning signs'}
              </h2>
              <p className="text-gray-400">
                {language === 'nl' 
                  ? 'Wees alert als je een van deze signalen herkent:'
                  : 'Be alert if you recognize any of these signs:'}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {warnings.map((warning, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-4 flex items-center gap-3 border border-red-500/30">
                  <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-500 text-sm font-bold">!</span>
                  </div>
                  <span className="text-gray-300">{warning}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-20 px-4 bg-black">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="w-12 h-12 text-rose-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'nl' ? 'Vermoed je fraude?' : 'Suspect fraud?'}
            </h2>
            <p className="text-gray-400 mb-6">
              {language === 'nl' 
                ? 'Neem direct contact met ons op. Wij nemen elke melding serieus en onderzoeken verdachte advertenties.'
                : 'Contact us immediately. We take every report seriously and investigate suspicious listings.'}
            </p>
            <a href="mailto:support@swipejewoning.nl" className="text-rose-400 font-semibold hover:text-rose-300 transition-colors">
              support@swipejewoning.nl
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SafetyPage;
